import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { promptLibraryApi, groupsApi, type PromptLibraryItem } from "@/services/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { VALID_MODELS, DEFAULT_AGENT_MODEL } from "@/lib/aiModels";
import {
  FileCode, Plus, Pencil, FlaskConical, Sparkles, Loader2, Settings2, Search, Filter, Archive, ArchiveRestore,
} from "lucide-react";

const TYPOLOGIES = [
  { value: "sales", label: "Vendas" },
  { value: "support", label: "Suporte" },
  { value: "community", label: "Comunidade" },
  { value: "internal_team", label: "Time Interno" },
  { value: "general", label: "Geral" },
];

const CATEGORIES = [
  { value: "general", label: "Geral" },
  { value: "summary", label: "Resumo" },
  { value: "faq", label: "FAQ" },
  { value: "sales", label: "Vendas" },
  { value: "support", label: "Suporte" },
  { value: "moderation", label: "Moderação" },
  { value: "analyzer", label: "Analyzer" },
];

interface FormState {
  name: string;
  description: string;
  template: string;
  variables: string;
  category: string;
  typology: string;
  tags: string;
  preferred_model: string;
  temperature_default: number;
}

const emptyForm: FormState = {
  name: "",
  description: "",
  template: "",
  variables: "",
  category: "general",
  typology: "general",
  tags: "",
  preferred_model: DEFAULT_AGENT_MODEL,
  temperature_default: 0.7,
};

function detectVariables(template: string): string[] {
  const matches = template.matchAll(/\{\{\s*([\w.-]+)\s*\}\}/g);
  return Array.from(new Set(Array.from(matches, (m) => m[1])));
}

export default function PromptLibrary() {
  const { toast } = useToast();
  const qc = useQueryClient();

  const [editing, setEditing] = useState<PromptLibraryItem | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [search, setSearch] = useState("");
  const [filterTypology, setFilterTypology] = useState<string>("all");
  const [view, setView] = useState<"active" | "archived">("active");

  // Playground state
  const [testing, setTesting] = useState<PromptLibraryItem | null>(null);
  const [testMode, setTestMode] = useState<"quick" | "simulate">("quick");
  const [testVars, setTestVars] = useState<Record<string, string>>({});
  const [testMessage, setTestMessage] = useState("");
  const [testGroupId, setTestGroupId] = useState<string>("");
  const [testResult, setTestResult] = useState<Awaited<ReturnType<typeof promptLibraryApi.test>> | null>(null);

  const { data: items = [], isLoading } = useQuery({
    queryKey: ["prompt-library", view],
    queryFn: view === "archived" ? promptLibraryApi.listArchived : promptLibraryApi.list,
  });
  const { data: groups = [] } = useQuery({ queryKey: ["groups"], queryFn: groupsApi.list });

  const filtered = useMemo(() => {
    return items.filter((p) => {
      if (filterTypology !== "all" && p.typology !== filterTypology) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        return p.name.toLowerCase().includes(q)
          || p.description.toLowerCase().includes(q)
          || p.template.toLowerCase().includes(q);
      }
      return true;
    });
  }, [items, filterTypology, search]);

  const createMut = useMutation({
    mutationFn: promptLibraryApi.create,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["prompt-library"] });
      setCreating(false);
      setForm(emptyForm);
      toast({ title: "Prompt criado" });
    },
    onError: (e: Error) => toast({ title: "Erro ao criar", description: e.message, variant: "destructive" }),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<PromptLibraryItem> }) => promptLibraryApi.update(id, updates),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["prompt-library"] });
      setEditing(null);
      toast({ title: "Prompt atualizado" });
    },
    onError: (e: Error) => toast({ title: "Erro ao atualizar", description: e.message, variant: "destructive" }),
  });

  const archiveMut = useMutation({
    mutationFn: promptLibraryApi.archive,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["prompt-library"] });
      toast({ title: "Prompt arquivado", description: "Você pode restaurá-lo na aba Arquivados." });
    },
    onError: (e: Error) => toast({ title: "Erro ao arquivar", description: e.message, variant: "destructive" }),
  });

  const unarchiveMut = useMutation({
    mutationFn: promptLibraryApi.unarchive,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["prompt-library"] });
      toast({ title: "Prompt restaurado" });
    },
    onError: (e: Error) => toast({ title: "Erro ao restaurar", description: e.message, variant: "destructive" }),
  });

  const testMut = useMutation({
    mutationFn: promptLibraryApi.test,
    onSuccess: (data) => setTestResult(data),
    onError: (e: Error) => toast({ title: "Falha no teste", description: e.message, variant: "destructive" }),
  });

  const openCreate = () => { setForm(emptyForm); setCreating(true); };

  const openEdit = (p: PromptLibraryItem) => {
    setForm({
      name: p.name,
      description: p.description,
      template: p.template,
      variables: (p.variables ?? []).join(", "),
      category: p.category || "general",
      typology: p.typology || "general",
      tags: (p.tags ?? []).join(", "),
      preferred_model: p.preferred_model || DEFAULT_AGENT_MODEL,
      temperature_default: Number(p.temperature_default ?? 0.7),
    });
    setEditing(p);
  };

  const handleSave = () => {
    if (!form.name.trim() || !form.template.trim()) {
      toast({ title: "Nome e template são obrigatórios", variant: "destructive" });
      return;
    }
    const detected = detectVariables(form.template);
    const declared = form.variables.split(",").map((v) => v.trim()).filter(Boolean);
    const variables = Array.from(new Set([...detected, ...declared]));
    const tags = form.tags.split(",").map((v) => v.trim()).filter(Boolean);

    const payload = {
      name: form.name.trim(),
      description: form.description.trim(),
      template: form.template,
      variables,
      tags,
      category: form.category,
      typology: form.typology === "general" ? null : form.typology,
      preferred_model: form.preferred_model,
      temperature_default: form.temperature_default,
    };

    if (editing) {
      updateMut.mutate({ id: editing.id, updates: payload as Partial<PromptLibraryItem> });
    } else {
      createMut.mutate(payload as any);
    }
  };

  const openTest = (p: PromptLibraryItem) => {
    const vars: Record<string, string> = {};
    (p.variables ?? []).forEach((v) => { vars[v] = ""; });
    setTesting(p);
    setTestVars(vars);
    setTestMessage("");
    setTestMode("quick");
    setTestGroupId("");
    setTestResult(null);
  };

  const runTest = () => {
    if (!testing) return;
    if (!testMessage.trim()) {
      toast({ title: "Escreva uma mensagem de teste", variant: "destructive" });
      return;
    }
    testMut.mutate({
      template: testing.template,
      variables: testVars,
      user_message: testMessage,
      model: testing.preferred_model || DEFAULT_AGENT_MODEL,
      temperature: testing.temperature_default ?? 0.7,
      mode: testMode,
      group_id: testMode === "simulate" && testGroupId ? testGroupId : null,
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <FileCode className="w-6 h-6 text-primary" /> Prompt Library
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Catálogo central de prompts da Syra. Cadastre, teste no playground e use em qualquer persona.
          </p>
        </div>
        <Button onClick={openCreate}><Plus className="w-4 h-4 mr-2" /> Novo Prompt</Button>
      </div>

      {/* View tabs */}
      <Tabs value={view} onValueChange={(v) => setView(v as "active" | "archived")}>
        <TabsList>
          <TabsTrigger value="active">Ativos</TabsTrigger>
          <TabsTrigger value="archived"><Archive className="w-3 h-3 mr-1" /> Arquivados</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por nome, descrição ou conteúdo..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={filterTypology} onValueChange={setFilterTypology}>
          <SelectTrigger className="w-full sm:w-[200px]">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas as tipologias</SelectItem>
            {TYPOLOGIES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => <Skeleton key={i} className="h-44 w-full" />)}
        </div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <FileCode className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>
              {items.length === 0
                ? view === "archived" ? "Nenhum prompt arquivado" : "Nenhum prompt na biblioteca ainda"
                : "Nenhum prompt encontrado"}
            </p>
            <p className="text-sm mt-1">
              {items.length === 0 ? "Crie seu primeiro prompt para começar" : "Ajuste os filtros ou crie um novo"}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((p) => {
            const typology = TYPOLOGIES.find((t) => t.value === p.typology)?.label;
            const modelLabel = VALID_MODELS.find((m) => m.id === p.preferred_model)?.label || p.preferred_model || "—";
            return (
              <Card key={p.id} className="hover:shadow-lg transition-all duration-300">
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between gap-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-primary shrink-0" /> {p.name}
                    </CardTitle>
                    <Badge variant="secondary" className="shrink-0">v{p.version}</Badge>
                  </div>
                  {p.description && (
                    <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{p.description}</p>
                  )}
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex flex-wrap gap-1">
                    {typology && <Badge variant="outline" className="text-xs">{typology}</Badge>}
                    <Badge variant="outline" className="text-xs">{p.category}</Badge>
                    {(p.tags ?? []).slice(0, 3).map((t) => (
                      <Badge key={t} variant="secondary" className="text-xs">{t}</Badge>
                    ))}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Modelo: {modelLabel} · Temp: {Number(p.temperature_default ?? 0.7).toFixed(1)}
                  </div>
                  {(p.variables ?? []).length > 0 && (
                    <div className="text-xs text-muted-foreground">
                      Variáveis: {(p.variables ?? []).map((v) => `{{${v}}}`).join(", ")}
                    </div>
                  )}
                  <div className="flex gap-1 pt-1">
                    <Button variant="outline" size="sm" onClick={() => openTest(p)}>
                      <FlaskConical className="w-3 h-3 mr-1" /> Testar
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => openEdit(p)}>
                      <Pencil className="w-3 h-3" />
                    </Button>
                    {view === "active" ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => archiveMut.mutate(p.id)}
                        disabled={archiveMut.isPending}
                        title="Arquivar prompt"
                      >
                        <Archive className="w-3 h-3" />
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => unarchiveMut.mutate(p.id)}
                        disabled={unarchiveMut.isPending}
                        title="Restaurar prompt"
                      >
                        <ArchiveRestore className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={creating || !!editing} onOpenChange={(o) => { if (!o) { setCreating(false); setEditing(null); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? `Editar: ${editing.name}` : "Novo prompt"}</DialogTitle>
            <DialogDescription>
              Use <code className="text-xs">{`{{nome_da_variavel}}`}</code> para placeholders. Variáveis são detectadas automaticamente.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Nome</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex: Resumo diário consultivo" />
              </div>
              <div className="space-y-1.5">
                <Label>Tipologia do grupo</Label>
                <Select value={form.typology} onValueChange={(v) => setForm({ ...form, typology: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TYPOLOGIES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Descrição</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="O que este prompt faz?" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Categoria</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Tags (separadas por vírgula)</Label>
                <Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="vendas, b2b, qualificação" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Template do prompt</Label>
              <Textarea
                value={form.template}
                onChange={(e) => setForm({ ...form, template: e.target.value })}
                rows={10}
                className="font-mono text-sm"
                placeholder={`Você é uma assistente da Syra no grupo {{grupo}}.\nTom: {{tom}}.\nRegra: nunca repita resumos anteriores.`}
              />
              {form.template && (
                <div className="text-xs text-muted-foreground">
                  Variáveis detectadas: {detectVariables(form.template).map((v) => `{{${v}}}`).join(", ") || "nenhuma"}
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Modelo preferido</Label>
                <Select value={form.preferred_model} onValueChange={(v) => setForm({ ...form, preferred_model: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {VALID_MODELS.map((m) => <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Temperatura: {form.temperature_default.toFixed(1)}</Label>
                <Slider
                  value={[form.temperature_default]}
                  onValueChange={([v]) => setForm({ ...form, temperature_default: v })}
                  min={0} max={2} step={0.1}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => { setCreating(false); setEditing(null); }}>Cancelar</Button>
              <Button onClick={handleSave} disabled={createMut.isPending || updateMut.isPending}>
                {(createMut.isPending || updateMut.isPending) && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editing ? "Salvar alterações" : "Criar prompt"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Playground */}
      <Dialog open={!!testing} onOpenChange={(o) => { if (!o) { setTesting(null); setTestResult(null); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FlaskConical className="w-5 h-5 text-primary" /> Playground · {testing?.name}
            </DialogTitle>
            <DialogDescription>
              Teste o prompt antes de aplicá-lo numa persona.
            </DialogDescription>
          </DialogHeader>

          <Tabs value={testMode} onValueChange={(v) => setTestMode(v as "quick" | "simulate")}>
            <TabsList className="grid grid-cols-2 w-full">
              <TabsTrigger value="quick">
                <Sparkles className="w-3 h-3 mr-1" /> Teste Rápido
              </TabsTrigger>
              <TabsTrigger value="simulate">
                <Settings2 className="w-3 h-3 mr-1" /> Simulação Completa
              </TabsTrigger>
            </TabsList>

            <TabsContent value="quick" className="space-y-3 mt-4">
              <p className="text-xs text-muted-foreground">
                Apenas o prompt + sua mensagem. Sem skills, sem memória.
              </p>
            </TabsContent>

            <TabsContent value="simulate" className="space-y-3 mt-4">
              <p className="text-xs text-muted-foreground">
                Carrega memória do grupo, política e tipologia. Resposta mais próxima do que sairia no WhatsApp.
              </p>
              <div className="space-y-1.5">
                <Label>Grupo de simulação</Label>
                <Select value={testGroupId} onValueChange={setTestGroupId}>
                  <SelectTrigger><SelectValue placeholder="Selecione um grupo..." /></SelectTrigger>
                  <SelectContent>
                    {groups.map((g: any) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </TabsContent>
          </Tabs>

          {/* Variables */}
          {Object.keys(testVars).length > 0 && (
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Variáveis</Label>
              {Object.keys(testVars).map((k) => (
                <div key={k} className="grid grid-cols-[120px_1fr] gap-2 items-center">
                  <code className="text-xs text-muted-foreground">{`{{${k}}}`}</code>
                  <Input
                    value={testVars[k]}
                    onChange={(e) => setTestVars({ ...testVars, [k]: e.target.value })}
                    placeholder={`Valor para ${k}`}
                  />
                </div>
              ))}
            </div>
          )}

          <div className="space-y-1.5">
            <Label>Mensagem do usuário</Label>
            <Textarea
              value={testMessage}
              onChange={(e) => setTestMessage(e.target.value)}
              rows={3}
              placeholder="Ex: Bom dia, qual o resumo de hoje?"
            />
          </div>

          <Button onClick={runTest} disabled={testMut.isPending} className="w-full">
            {testMut.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Rodar teste
          </Button>

          {testResult && (
            <div className="space-y-3 border-t pt-4">
              <div className="flex flex-wrap gap-2 text-xs">
                <Badge variant="outline">Modelo: {testResult.model_used}</Badge>
                {testResult.provider_used && <Badge variant="outline">Provedor: {testResult.provider_used}</Badge>}
                <Badge variant="outline">{testResult.latency_ms}ms</Badge>
                {testResult.tokens_in != null && (
                  <Badge variant="outline">{testResult.tokens_in}→{testResult.tokens_out} tokens</Badge>
                )}
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Resposta</Label>
                <div className="rounded-lg border bg-muted/30 p-3 text-sm whitespace-pre-wrap">
                  {testResult.response || <em className="text-muted-foreground">(resposta vazia)</em>}
                </div>
              </div>

              <details className="text-xs">
                <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                  Ver system prompt renderizado e contexto da simulação
                </summary>
                <pre className="mt-2 rounded-lg bg-muted/50 p-2 overflow-x-auto whitespace-pre-wrap">
                  {testResult.rendered_system_prompt}
                </pre>
                <pre className="mt-2 rounded-lg bg-muted/50 p-2 overflow-x-auto">
                  {JSON.stringify(testResult.simulation_context, null, 2)}
                </pre>
              </details>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
